import React, { useState, useRef, useEffect } from 'react';
import useStore from '../../../store';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { TbArrowsDiagonal2 } from 'react-icons/tb';

import './Reference.css';

const calculateDistance = (point1, point2) => {
  const xDiff = point1.x - point2.x;
  const yDiff = point1.y - point2.y;
  return Math.sqrt(xDiff * xDiff + yDiff * yDiff);
};

const calculateCenter = (touch1, touch2) => {
  return {
    x: (touch1.x + touch2.x) / 2,
    y: (touch1.y + touch2.y) / 2,
  };
};

const calculateAngle = (touch1, touch2) => {
  const dx = touch2.clientX - touch1.clientX;
  const dy = touch2.clientY - touch1.clientY;
  return Math.atan2(dy, dx) * 180 / Math.PI;
};

const getImageBrightness = (canvas) => {
  if (!canvas) return 0.5;
  
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  
  // Берём 5 ключевых точек: 4 угла + центр
  const points = [
    [0, 0],                    // левый верхний
    [width - 1, 0],            // правый верхний  
    [0, height - 1],           // левый нижний
    [width - 1, height - 1],   // правый нижний
    [Math.floor(width / 2), Math.floor(height / 2)] // центр
  ];
  
  let totalBrightness = 0;
  let validPoints = 0;
  
  for (const [x, y] of points) {
    try {
      const imageData = ctx.getImageData(x, y, 1, 1);
      const [r, g, b] = imageData.data;
      // Используем стандартную формулу яркости
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      totalBrightness += brightness;
      validPoints++;
    } catch (e) {
      // Игнорируем ошибки (например, если координаты вне границ)
    }
  }
  
  return validPoints > 0 ? totalBrightness / validPoints : 0.5;
};

const drawReferenceGrid = (ctx, canvasWidth, canvasHeight, gridSize, imageCanvas) => {
  if (!gridSize || gridSize <= 0) return;
  
  ctx.save();
  
  const imageBrightness = getImageBrightness(imageCanvas);
  ctx.strokeStyle = imageBrightness > 0.4 ? 'rgba(0, 0, 0)' : 'rgb(255, 255, 255)';
  const baseLineWidth = 1;
  ctx.setLineDash([]);

  const drawLines = (predicate, lineWidth) => {
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    // Вертикальные линии
    for (let i = 0; ; i++) {
      const x = i * gridSize;
      if (x > canvasWidth) break;
      if (predicate(i)) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
      }
    }
    // Горизонтальные линии
    for (let i = 0; ; i++) {
      const y = i * gridSize;
      if (y > canvasHeight) break;
      if (predicate(i)) {
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
      }
    }
    ctx.stroke();
  };

  const decimalGrid = true;

  if (decimalGrid) {
    // Сначала тонкие линии (не кратные 10)
    drawLines(i => i % 10 !== 0, baseLineWidth);
    // Затем толстые (кратные 10, включая нулевую)
    drawLines(i => i % 10 === 0, baseLineWidth * 2);
  } else {
    // Все линии одной толщины
    drawLines(() => true, baseLineWidth);
  }
  
  ctx.restore();
};

const Reference = ({ store }) => {
  const setState = useStore.setState;

  const { ref } = store;
  const { menu, convy } = ref;
  const { keyboard } = convy;

  menu.source = menu.source || {};
  const source = menu.source;

  // Получаем настройки сетки
  const commonSettings = ref.brush?.methods?.brushSetsFun?.('common') || {};
  const { showCommonGrid, referenceGridSize } = commonSettings;

  const [, forceUpdate] = useState(0);
  const update = () => forceUpdate(n => n + 1);

  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const handleClose = () => {
    setState({ showReference: false });
  };

  const handleDelete = () => {
    menu.source = {};
    update();
  };

  const calculateFitScale = (source) => {
    return Math.min(source.displayWidth / source.naturalWidth, source.displayHeight / source.naturalHeight);
  };

  const centerImage = () => {
    const fitScale = calculateFitScale(source);
    source.panOffsetX = 0;
    source.panOffsetY = 0;
    source.zoomScale = fitScale;
  };

  const drawReference = () => {
    if (!canvasRef.current || !source.canvas) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, source.displayWidth, source.displayHeight);
    
    // Отрисовка изображения и сетки в одной системе координат
    ctx.save();
    ctx.translate(source.displayWidth / 2 + source.panOffsetX, source.displayHeight / 2 + source.panOffsetY);
    ctx.rotate(source.rotation * Math.PI / 180);
    ctx.scale(source.zoomScale, source.zoomScale);
    ctx.translate(-source.naturalWidth / 2, -source.naturalHeight / 2);
    
    // Рисуем изображение
    ctx.drawImage(
      source.canvas,
      0,
      0,
      source.naturalWidth,
      source.naturalHeight
    );

    // Рисуем сетку в той же системе координат (поверх изображения)
    if (showCommonGrid && referenceGridSize) {
      drawReferenceGrid(ctx, source.naturalWidth, source.naturalHeight, referenceGridSize, source.canvas);
    }
    
    ctx.restore();
  };

  useEffect(() => {
    drawReference();
  }, [source.displayWidth, source.displayHeight, source.panOffsetX, source.panOffsetY, source.zoomScale, source.rotation, showCommonGrid, referenceGridSize]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (source.isRefZoomDrag) {
        source.isRefZoomDrag = false;
        update();
      }
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const handleUrlInputBlur = (e) => {
    const url = e.target.value.trim();
    if (url) {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const maxWidth = window.innerWidth * 0.5;
        const maxHeight = window.innerHeight * 0.3;
        let displayWidth = img.naturalWidth;
        let displayHeight = img.naturalHeight;
        const widthRatio = displayWidth / maxWidth;
        const heightRatio = displayHeight / maxHeight;
        const ratio = Math.max(widthRatio, heightRatio, 1);
        displayWidth /= ratio;
        displayHeight /= ratio;

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const zoomScale = Math.min(displayWidth / img.naturalWidth, displayHeight / img.naturalHeight);

        menu.source = {
          image: url,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          displayWidth,
          displayHeight,
          aspectRatio: img.naturalHeight / img.naturalWidth,
          x: 20,
          y: 20,
          dragging: false,
          panning: false,
          resizing: false,
          resizingByHandle: false,
          pinchStartDistance: null,
          initialWidth: displayWidth,
          initialHeight: displayHeight,
          initialX: null,
          initialY: null,
          zoomScale,
          panOffsetX: 0,
          panOffsetY: 0,
          rotation: 0,
          initialZoomScale: null,
          initialPanOffsetX: null,
          initialPanOffsetY: null,
          initialRotation: null,
          initialDistance: null,
          initialCenter: null,
          zoomModified: false,
          isRefZoomDrag: false,
          canvas,
        };
        update();
      };
      img.onerror = () => {};
      img.src = url;
    }
  };

  const processImage = (dataUrl, callback) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const maxWidth = window.innerWidth * 0.5;
      const maxHeight = window.innerHeight * 0.3;
      let displayWidth = img.naturalWidth;
      let displayHeight = img.naturalHeight;
      const widthRatio = displayWidth / maxWidth;
      const heightRatio = displayHeight / maxHeight;
      const ratio = Math.max(widthRatio, heightRatio, 1);
      displayWidth /= ratio;
      displayHeight /= ratio;

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const zoomScale = Math.min(displayWidth / img.naturalWidth, displayHeight / img.naturalHeight);

      menu.source = {
        image: dataUrl,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        displayWidth,
        displayHeight,
        aspectRatio: img.naturalHeight / img.naturalWidth,
        x: 20,
        y: 20,
        dragging: false,
        panning: false,
        resizing: false,
        resizingByHandle: false,
        pinchStartDistance: null,
        initialWidth: displayWidth,
        initialHeight: displayHeight,
        initialX: null,
        initialY: null,
        zoomScale,
        panOffsetX: 0,
        panOffsetY: 0,
        rotation: 0,
        initialZoomScale: null,
        initialPanOffsetX: null,
        initialPanOffsetY: null,
        initialRotation: null,
        initialDistance: null,
        initialCenter: null,
        zoomModified: false,
        isRefZoomDrag: false,
        canvas,
      };
      update();
      if (callback) callback();
    };
    img.src = dataUrl;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        processImage(event.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Выбранный файл не является изображением");
    }
  };

  const handlePaste = (e) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    for (const item of items) {
      if (item.type.indexOf("image") === 0) {
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (event) => {
          processImage(event.target.result, () => {
            e.target.value = '';
          });
        };
        reader.readAsDataURL(blob);
        e.preventDefault();
        break;
      }
    }
  };

  const handleTouchStart = (e) => {
    if (useStore.getState().activeTool === 'pipette') { return; }
    if (!source.image) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    if (e.touches.length === 1) {
      source.dragging = true;
      source.initialX = e.touches[0].clientX - source.x;
      source.initialY = e.touches[0].clientY - source.y;
    } else if (e.touches.length === 2) {
      source.resizing = true;

      const touch1 = { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top, clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
      const touch2 = { x: e.touches[1].clientX - rect.left, y: e.touches[1].clientY - rect.top, clientX: e.touches[1].clientX, clientY: e.touches[1].clientY };

      source.initialDistance = calculateDistance(touch1, touch2);
      source.initialCenter = calculateCenter(touch1, touch2);
      source.initialZoomScale = source.zoomScale;
      source.initialPanOffsetX = source.panOffsetX;
      source.initialPanOffsetY = source.panOffsetY;
      source.initialRotation = source.rotation;
      source.initialTouchAngle = calculateAngle(e.touches[0], e.touches[1]);
      source.zoomInitialized = false;
      source.rotationInitialized = false;
      source.offsetInitialized = false;

      // Compute pivot_rel
      const localX = source.initialCenter.x;
      const localY = source.initialCenter.y;
      const dx = localX - (source.displayWidth / 2 + source.initialPanOffsetX);
      const dy = localY - (source.displayHeight / 2 + source.initialPanOffsetY);
      const rad = source.initialRotation * Math.PI / 180;
      const unrotX = dx * Math.cos(-rad) - dy * Math.sin(-rad);
      const unrotY = dx * Math.sin(-rad) + dy * Math.cos(-rad);
      const pivotImageX = unrotX / source.initialZoomScale + source.naturalWidth / 2;
      const pivotImageY = unrotY / source.initialZoomScale + source.naturalHeight / 2;
      source.pivot_relX = pivotImageX - source.naturalWidth / 2;
      source.pivot_relY = pivotImageY - source.naturalHeight / 2;
    }
    update();
  };

  const handleTouchMove = (e) => {
    if (useStore.getState().activeTool === 'pipette') { return; }
    if (!source.image) return;
    if (keyboard.controlIsDown) { return }

    const rect = wrapperRef.current.getBoundingClientRect();

    if (source.dragging && e.touches.length === 1) {
      const touch = e.touches[0];
      source.x = touch.clientX - source.initialX;
      source.y = touch.clientY - source.initialY;
      update();
    } else if (source.resizing && e.touches.length === 2) {

      const touch1 = { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top, clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
      const touch2 = { x: e.touches[1].clientX - rect.left, y: e.touches[1].clientY - rect.top, clientX: e.touches[1].clientX, clientY: e.touches[1].clientY };

      let distance = calculateDistance(touch1, touch2);
      let newCenter = calculateCenter(touch1, touch2);
      let currentAngle = calculateAngle(e.touches[0], e.touches[1]);
      let angleChange = currentAngle - source.initialTouchAngle;

      if (!source.rotationInitialized && Math.abs(angleChange) > 10) {
        source.rotationInitialized = true;
        source.initialTouchAngle = currentAngle;
        angleChange = 0;
      }
      if (!source.rotationInitialized) {
        angleChange = 0;
      }

      let newRotation = source.initialRotation + angleChange;
      if (source.rotationInitialized) {
        let rem = newRotation % 360;
        if (rem < 0) rem += 360;
        const cards = [0, 90, 180, 270];
        for (let card of cards) {
          let diff = card === 0 ? Math.min(rem, 360 - rem) : Math.abs(rem - card);
          if (diff <= 5) {
            newRotation = newRotation - rem + card;
            break;
          }
        }
      }

      if (!source.zoomInitialized && (distance / source.initialDistance > 1.15 || distance / source.initialDistance < 0.85)) {
        source.zoomInitialized = true;
        source.initialDistance = distance;
      } else if (!source.zoomInitialized) {
        distance = source.initialDistance;
      }

      let newZoomScale = source.initialZoomScale * (distance / source.initialDistance);
      const fitScale = calculateFitScale(source);
      newZoomScale = Math.max(fitScale, newZoomScale);
      newZoomScale = Math.min(10, newZoomScale);
      if (source.zoomInitialized) source.zoomModified = true;

      const centerDist = calculateDistance(newCenter, source.initialCenter);
      if (!source.offsetInitialized && centerDist > 15) {
        source.offsetInitialized = true;
        source.initialCenter = newCenter;
      } else if (!source.offsetInitialized) {
        newCenter = source.initialCenter;
      }

      const offsetDelta = {
        x: newCenter.x - source.initialCenter.x,
        y: newCenter.y - source.initialCenter.y,
      };

      const radInitial = source.initialRotation * Math.PI / 180;
      const radNew = newRotation * Math.PI / 180;

      const rotatedInitial = {
        x: source.pivot_relX * Math.cos(radInitial) - source.pivot_relY * Math.sin(radInitial),
        y: source.pivot_relX * Math.sin(radInitial) + source.pivot_relY * Math.cos(radInitial),
      };

      const rotatedNew = {
        x: source.pivot_relX * Math.cos(radNew) - source.pivot_relY * Math.sin(radNew),
        y: source.pivot_relX * Math.sin(radNew) + source.pivot_relY * Math.cos(radNew),
      };

      const newPanOffsetX = source.initialPanOffsetX + (source.initialZoomScale * rotatedInitial.x - newZoomScale * rotatedNew.x) + offsetDelta.x;
      const newPanOffsetY = source.initialPanOffsetY + (source.initialZoomScale * rotatedInitial.y - newZoomScale * rotatedNew.y) + offsetDelta.y;

      source.zoomScale = newZoomScale;
      source.rotation = newRotation;
      source.panOffsetX = newPanOffsetX;
      source.panOffsetY = newPanOffsetY;
      update();
    }
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (useStore.getState().activeTool === 'pipette') { return; }

    source.dragging = false;
    source.resizing = false;
    source.initialDistance = null;
    source.initialCenter = null;

    const fitScale = calculateFitScale(source);
    if (Math.abs(source.zoomScale - fitScale) < 0.01) {
      source.zoomScale = fitScale;
      source.zoomModified = false;
      source.rotation = 0;
      // Force proportional aspect
      source.displayHeight = source.displayWidth * source.aspectRatio;
      centerImage();
    }
    update();
  };

  const handleResizeMouseDown = (e) => {
    if (useStore.getState().activeTool === 'pipette') { return; }

    e.stopPropagation();
    e.preventDefault();
    source.resizingByHandle = true;
    source.resizeStartX = e.clientX;
    source.resizeStartY = e.clientY;
    source.initialWidthHandle = source.displayWidth;
    source.initialHeightHandle = source.displayHeight;

    const handleMouseMove = (e) => {
      if (!source.resizingByHandle) return;
      const dx = e.clientX - source.resizeStartX;
      const dy = e.clientY - source.resizeStartY;
      if (!source.zoomModified) {
        // Proportional resize
        const scaleX = (source.initialWidthHandle + dx) / source.initialWidthHandle;
        const scaleY = (source.initialHeightHandle + dy) / source.initialHeightHandle;
        const scale = Math.min(scaleX, scaleY);
        if (scale > 0) {
          source.displayWidth = source.initialWidthHandle * scale;
          source.displayHeight = source.initialHeightHandle * scale;
        }
        // Update zoomScale and center
        centerImage();
      } else {
        // Free resize
        source.displayWidth = Math.max(50, source.initialWidthHandle + dx);
        source.displayHeight = Math.max(50, source.initialHeightHandle + dy);
      }
      update();
    };

    const handleMouseUp = () => {
      source.resizingByHandle = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeTouchStart = (e) => {
    if (useStore.getState().activeTool === 'pipette') { return; }
    
    e.stopPropagation();
    e.preventDefault();
    if (e.touches.length === 1) {
      source.resizingByHandle = true;
      source.resizeStartX = e.touches[0].clientX;
      source.resizeStartY = e.touches[0].clientY;
      source.initialWidthHandle = source.displayWidth;
      source.initialHeightHandle = source.displayHeight;
    }
    update();
  };

  const handleResizeTouchMove = (e) => {
    
    if (!source.resizingByHandle || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const dx = touch.clientX - source.resizeStartX;
    const dy = touch.clientY - source.resizeStartY;
    if (!source.zoomModified) {
      // Proportional
      const scaleX = (source.initialWidthHandle + dx) / source.initialWidthHandle;
      const scaleY = (source.initialHeightHandle + dy) / source.initialHeightHandle;
      const scale = Math.min(scaleX, scaleY);
      if (scale > 0) {
        source.displayWidth = source.initialWidthHandle * scale;
        source.displayHeight = source.initialHeightHandle * scale;
      }
      centerImage();
    } else {
      // Free
      source.displayWidth = Math.max(50, source.initialWidthHandle + dx);
      source.displayHeight = Math.max(50, source.initialHeightHandle + dy);
    }
    update();
    e.preventDefault();
  };

  const handleResizeTouchEnd = () => {
    
    source.resizingByHandle = false;
    update();
  };

  if (!store.showReference) return null;

  const renderPromptContainer = () => {
    return (
      <div className="reference-prompt-container">
        <div className="reference-title">{window.t('reference.title')}</div>
        <input
          type="text"
          className="reference-url-input-field input-field"
          placeholder={window.t('reference.placeholder')}
          onChange={handleUrlInputBlur}
          onBlur={handleUrlInputBlur}
          onPaste={handlePaste}
        />
        <div className="message-popup-button-container">
          <button
            className="message-popup-button cancel-button"
            onClick={handleClose}
          >
            {window.t('reference.button_cancel')}
          </button>
          <label className="message-popup-button">
            {window.t('reference.button_open_file')}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>
    );
  };

  const renderReferenceImage = () => {
    return (
      <div className="reference-container">
        <div
          ref={wrapperRef}
          className="reference-image-wrapper"
          style={{
            position: 'absolute',
            left: source.x,
            top: source.y,
            width: source.displayWidth,
            height: source.displayHeight,
            overflow: 'hidden',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onContextMenu={(e) => e.preventDefault()}
          onMouseDown={(e) => {
            if (useStore.getState().activeTool === 'pipette') { return; }

            const isPanning = (e.button === 0 && keyboard.spaceIsDown) || e.button === 2;

            if (isPanning && source.zoomModified) {
              e.preventDefault();
              source.panning = true;
              source.panStartX = e.clientX;
              source.panStartY = e.clientY;
              source.initialPanOffsetX = source.panOffsetX;
              source.initialPanOffsetY = source.panOffsetY;
              const handleMouseMove = (e) => {
                if (!source.panning) return;
                const dx = e.clientX - source.panStartX;
                const dy = e.clientY - source.panStartY;
                source.panOffsetX = source.initialPanOffsetX + dx;
                source.panOffsetY = source.initialPanOffsetY + dy;
                update();
              };
              const handleMouseUp = () => {
                source.panning = false;
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
              };
              window.addEventListener('mousemove', handleMouseMove);
              window.addEventListener('mouseup', handleMouseUp);
            } else {
              e.preventDefault();
              source.dragging = true;
              source.initialX = e.clientX - source.x;
              source.initialY = e.clientY - source.y;
              const handleMouseMove = (e) => {
                if (keyboard.controlIsDown) { update(); return }
                source.x = e.clientX - source.initialX;
                source.y = e.clientY - source.initialY;
                update();
              };
              const handleMouseUp = () => {
                source.dragging = false;
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
              };
              window.addEventListener('mousemove', handleMouseMove);
              window.addEventListener('mouseup', handleMouseUp);
            }
          }}
        >
          <canvas
            ref={canvasRef}
            width={source.displayWidth}
            height={source.displayHeight}
            onPointerDown={(e) => {
              if (useStore.getState().activeTool === 'pipette') e.stopPropagation();
              ref.drawing.methods.startPickingColorFromCanvas({ nativeEvent: e, fromCanvas: canvasRef.current });
            }}
            onPointerMove={(e) => {
              if (useStore.getState().activeTool === 'pipette') e.stopPropagation();
              ref.drawing.methods.searchForColorFromCanvas({ nativeEvent: e, fromCanvas: canvasRef.current });
            }}
            onPointerUp={(e) => {
              if (useStore.getState().activeTool === 'pipette') e.stopPropagation();
              ref.drawing.methods.pickColorFromCanvas({ nativeEvent: e });
            }}
          />
          <button
            className="reference-close-button"
            onClick={handleClose}
          >
            <FaTimes className="reference-button-icon" />
          </button>
          <button
            className="reference-delete-button"
            onClick={handleDelete}
          >
            <FaTrash className="reference-button-icon" />
          </button>
          <div
            className="reference-resize-handle"
            onMouseDown={handleResizeMouseDown}
            onTouchStart={handleResizeTouchStart}
            onTouchMove={handleResizeTouchMove}
            onTouchEnd={handleResizeTouchEnd}
          >
            <TbArrowsDiagonal2 />
          </div>
        </div>
      </div>
    );
  };

  return (
    !source.image ? (
      renderPromptContainer()
    ) : (
      renderReferenceImage()
    )
  );
};

export default Reference;

